// The five-kind color system — one stable hue per kind, used
// everywhere a kind appears (Inbox, detail, Instruments) so the
// palette itself teaches the concept model. The values live in
// index.css as `--s-kind-*` and are re-inked per theme; nothing
// here may name a hex.

import type { IssueSummary } from '../lib/api';
import { Badge } from '../../shared/design/controls';
import { useLocale, useT } from '../i18n';

export const kindColor = (kind: IssueSummary['kind']): string =>
  `var(--s-kind-${kind})`;

/** A 12% tint of the kind hue — badge fill, row accents. */
export const kindTint = (kind: IssueSummary['kind'], pct = 12): string =>
  `color-mix(in srgb, var(--s-kind-${kind}) ${pct}%, transparent)`;

export function KindBadge({ kind }: { kind: IssueSummary['kind'] }) {
  const tones = { error: 'danger', warn: 'warning', trace: 'info', assert: 'accent', probe: 'success' } as const;
  return <Badge tone={tones[kind]}>{kind}</Badge>;
}

export function RegressedBadge() {
  const t = useT();
  return <Badge tone="danger">{t('inbox.groupRegressed')}</Badge>;
}

/** breadth × depth, the objective importance pair.
 *  Events without a userKey (pre-identify boot signals) make the
 *  breadth zero — "0u×0" is noise, so the pair collapses to the
 *  event count alone.
 *
 *  The title carries the population and the window, because three
 *  places in this console print a number of users and all three mean
 *  something different: this one is all-time and counts whoever
 *  reported *this issue*, the project card counts anyone who sent any
 *  event in 24h, and the crash-free card counts whoever started a
 *  session. A reader comparing 5 here with 3 there was reading a
 *  contradiction that is not one. */
export function ImpactCell({
  users,
  maxPerUser,
  events,
  title,
}: {
  users: number;
  maxPerUser: number;
  events: number;
  title?: string;
}) {
  const t = useT();
  const locale = useLocale();
  const format = (n: number) => n.toLocaleString(locale);
  return (
    <span className="text-xs tabular-nums text-fg-muted" title={title ?? t('impact.maximum', { max: format(maxPerUser) })}>
      {users > 0 ? `${t('impact.users', { count: format(users) })} · ` : ''}
      {t('impact.events', { count: format(events) })}
    </span>
  );
}
